const services = [
  {
    icon: "ri-temp-cold-line",
    title: "Industrial Machinery",
    desc: "Installation and repair services for compressors, vacuum pumps, air conditioning systems, cold rooms, chillers and Building Management Systems (BMS).",
  },
  {
    icon: "ri-tools-line",
    title: "Preventive Maintenance",
    desc: "Scheduled preventive maintenance programs tailored to your equipment — keeping downtime to zero and your production running at peak efficiency.",
  },
  {
    icon: "ri-settings-3-line",
    title: "Pneumatic & Hydraulic Systems",
    desc: "Complete installation and maintenance of pneumatic systems and hydraulic systems for industrial and manufacturing environments.",
  },
  {
    icon: "ri-building-2-line",
    title: "Structural Installation",
    desc: "Professional structural installation works, delivering robust, safe, and compliant industrial structures for your operations.",
  },
  {
    icon: "ri-plant-line",
    title: "Food Grade Equipment",
    desc: "Installation of food grade mills, conveyors, elevators, bins, storage silos and dehumidification equipment meeting international food safety standards.",
  },
  {
    icon: "ri-drop-line",
    title: "Water Treatment Plants",
    desc: "Installation and maintenance of water treatment plants including Reverse Osmosis (RO), Demineralization (DM) and dosing pump systems.",
  },
  {
    icon: "ri-cpu-line",
    title: "System Automation",
    desc: "PLC, SCADA and datalogger installation with real-time alerts — bringing smart monitoring and full automation to your industrial operations.",
  },
];

const stats = [
  { value: "15+", label: "Years of Excellence" },
  { value: "500+", label: "Projects Completed" },
  { value: "200+", label: "Satisfied Clients" },
  { value: "8", label: "Core Services" },
];

export default function AboutProfile() {
  return (
    <section className="py-24 bg-white">
      <div className="max-w-7xl mx-auto px-6">

        {/* ── Section header ── */}
        <div className="text-center mb-16">
          <span className="inline-block bg-cyan-50 text-cyan-600 text-xs font-semibold uppercase tracking-widest px-4 py-1.5 rounded-full">
            Company Profile
          </span>
          <h2 className="text-4xl md:text-5xl font-extrabold text-gray-900 mt-4 leading-tight">
            Who Are <span className="text-cyan-600">We?</span>
          </h2>
          <p className="max-w-2xl mx-auto mt-5 text-gray-500 text-sm leading-relaxed">
            A trusted name in industrial engineering across East Africa — delivering precision,
            reliability and world-class expertise to every project we undertake.
          </p>
        </div>

        {/* ── Image + Intro block ── */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center mb-20">

          {/* Images mosaic */}
          <div className="relative">
            {/* Main large image */}
            <div className="w-full h-[420px] rounded-lg overflow-hidden">
              <img
                src="/images/hero/engineering.webp"
                alt="ISMAN Company world-class industrial manufacturing facility"
                className="w-full h-full object-cover object-center"
              />
            </div>
            {/* Inset second image */}
            <div className="absolute bottom-5 right-5 w-44 h-32 rounded-lg overflow-hidden border-4 border-white hidden md:block">
              <img
                src="/images/welding/tig.webp"
                alt="ISMAN engineering team at work"
                className="w-full h-full object-cover object-top"
              />
            </div>
            {/* Years badge */}
            <div className="absolute top-5 -left-4 bg-cyan-600 text-white rounded-lg px-5 py-4 hidden md:block">
              <p className="text-3xl font-extrabold leading-none">15+</p>
              <p className="text-xs uppercase tracking-widest text-cyan-100 mt-1">Years of<br />Excellence</p>
            </div>
          </div>

          {/* Intro text */}
          <div>
            <h3 className="text-2xl font-bold text-gray-900 mb-4 leading-snug">
              A Team of Highly Skilled Engineers &amp; Technicians
            </h3>
            <p className="text-gray-600 text-sm leading-relaxed mb-5">
              <strong className="text-gray-800">ISMAN COMPANY</strong> is a team of highly skilled engineers and technicians
              with hands-on experience in industrial equipment and production lines. We have built our reputation on delivering
              engineering excellence that East African industries rely on — from food processing plants and HVAC systems to
              full automation and water treatment solutions.
            </p>
            <p className="text-gray-600 text-sm leading-relaxed mb-8">
              Our deep technical expertise, combined with a commitment to honesty and professionalism, has earned us the trust
              of over <strong className="text-gray-800">200+ clients</strong> across manufacturing, hospitality, healthcare,
              and agriculture sectors. Every project we touch is treated with the same level of care and precision — whether
              it's a routine maintenance call or a full industrial installation.
            </p>

            {/* Stats row */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
              {stats.map((s, i) => (
                <div key={i} className="bg-gray-50 rounded-lg p-4 text-center border border-gray-100">
                  <p className="text-2xl font-extrabold text-cyan-600">{s.value}</p>
                  <p className="text-xs text-gray-500 mt-1 leading-snug">{s.label}</p>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* ── Services We Offer ── */}
        <div className="bg-gray-50 rounded-2xl p-10 mb-20 border border-gray-100">
          <div className="text-center mb-10">
            <h3 className="text-2xl font-bold text-gray-900">Our Services Include</h3>
            <p className="text-gray-500 text-sm mt-2">
              End-to-end industrial engineering solutions — from installation to automation
            </p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {services.map((svc, i) => (
              <div
                key={i}
                className="bg-white rounded-xl p-6 border border-gray-100 hover:border-cyan-200 transition-all group"
              >
                <div className="w-11 h-11 flex items-center justify-center bg-cyan-50 rounded-full mb-4 group-hover:bg-cyan-600 transition-colors">
                  <i className={`${svc.icon} text-cyan-600 group-hover:text-white text-lg`}></i>
                </div>
                <h4 className="text-gray-800 font-semibold text-sm mb-2">{svc.title}</h4>
                <p className="text-gray-500 text-xs leading-relaxed">{svc.desc}</p>
              </div>
            ))}
          </div>
        </div>

        {/* ── Full width second photo banner ── */}
        <div className="relative w-full h-64 rounded-2xl overflow-hidden mb-20">
          <img
            src="/images/kitchen/installation-team.webp"
            alt="ISMAN Company engineering services team"
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-cyan-950/80 via-cyan-900/50 to-transparent flex items-center px-10 md:px-16">
            <div>
              <p className="text-cyan-300 text-xs uppercase tracking-widest font-semibold mb-2">Our Commitment</p>
              <h3 className="text-white text-2xl md:text-3xl font-extrabold leading-tight max-w-lg">
                Delivering Reliable Engineering <br className="hidden md:block" />
                Solutions Since Day One
              </h3>
              <p className="text-cyan-100 text-sm mt-3 max-w-md leading-relaxed">
                From preventive maintenance to full plant automation — ISMAN COMPANY is your
                single source for dependable industrial engineering in East Africa.
              </p>
            </div>
          </div>
        </div>

        {/* ── Why Choose Us ── */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          <div className="lg:col-span-1">
            <span className="text-cyan-600 text-xs font-semibold uppercase tracking-widest">Why Choose Us</span>
            <h3 className="text-2xl font-bold text-gray-900 mt-3 leading-snug">
              The ISMAN Advantage — Precision You Can Trust
            </h3>
            <p className="text-gray-500 text-sm mt-4 leading-relaxed">
              We don't just fix machines — we ensure your entire operation performs at its best. Our engineers are
              continuously trained on the latest technologies, bringing cutting-edge solutions to every challenge you face.
            </p>
            <div className="mt-6 flex items-center gap-3">
              <div className="w-11 h-11 flex items-center justify-center bg-cyan-600 rounded-full shrink-0">
                <i className="ri-award-line text-white text-lg"></i>
              </div>
              <div>
                <p className="text-gray-800 text-sm font-semibold">Certified Engineering Excellence</p>
                <p className="text-gray-500 text-xs">Industrial · HVAC · Fabrication · Automation</p>
              </div>
            </div>
          </div>

          <div className="lg:col-span-2 grid grid-cols-1 sm:grid-cols-2 gap-6">
            {[
              {
                icon: "ri-user-star-line",
                title: "Expert Manpower",
                desc: "Our team consists of mechanical, electrical, and electronics technicians who are constantly under training to handle the latest industrial challenges.",
              },
              {
                icon: "ri-shield-check-line",
                title: "Reliability & Durability",
                desc: "We consider all technical requirements ensuring our repair and installation outcomes are as reliable and durable as possible — proven against future failures.",
              },
              {
                icon: "ri-time-line",
                title: "Minimal Downtime",
                desc: "Fast response, accurate diagnostics and efficient execution mean your production line gets back up and running with the least possible disruption.",
              },
              {
                icon: "ri-hand-heart-line",
                title: "Honesty is Our Policy",
                desc: "We operate with full transparency — fair pricing, honest assessments and no shortcuts. Our company ethos is to be honorable and conscientious to all stakeholders.",
              },
            ].map((item, i) => (
              <div key={i} className="flex items-start gap-4 bg-gray-50 rounded-xl p-5 border border-gray-100">
                <div className="w-10 h-10 flex items-center justify-center bg-cyan-600 rounded-full shrink-0">
                  <i className={`${item.icon} text-white text-base`}></i>
                </div>
                <div>
                  <p className="text-gray-800 font-semibold text-sm mb-1">{item.title}</p>
                  <p className="text-gray-500 text-xs leading-relaxed">{item.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>

      </div>
    </section>
  );
}
